import * as XLSX from "xlsx-js-style";

/**
 * Универсальная функция экспорта данных в Excel с поддержкой summary.
 *
 * @param {Array<{ title?: string, colTitle?: string, columns: Array, data: Array, summary?: Array<Array<string|number>> }>} tables
 * @param {string} fileName — имя итогового файла
 */
export const exportToExcel = (tablesInput, fileName = "export.xlsx") => {
  const tables = Array.isArray(tablesInput) ? tablesInput : [tablesInput];
  const workbook = XLSX.utils.book_new();

  // Храним последний вариант таблицы для каждого title
  const lastTablesMap = new Map();
  tables.forEach((table) => {
    if (!table || !table.data?.length) return;
    const { title } = table;
    if (title) lastTablesMap.set(title, table);
  });

  Array.from(lastTablesMap.values()).forEach((table, index) => {
    const { title, colTitle, columns, data, summary } = table;
    // Заголовки
    const headers = columns.map((col) => {
      if (typeof col.title === "string") return col.title;
      if (typeof col.title === "number") return String(col.title);
      if (col.title?.props?.children) {
        if (Array.isArray(col.title.props.children))
          return col.title.props.children.filter((c) => typeof c === "string").join(" ");
        return String(col.title.props.children);
      }
      return col.dataIndex || "";
    });

    // Данные
    const rows = data.map((row) =>
      columns.map((col) => {
        let value = "";
        if (typeof col.render === "function") {
          try {
            const rendered = col.render(row[col.dataIndex], row);
            if (typeof rendered === "string" || typeof rendered === "number") value = rendered;
            else if (Array.isArray(rendered)) value = rendered.join(", ");
            else if (rendered?.props?.children) {
              if (Array.isArray(rendered.props.children))
                value = rendered.props.children.filter((c) => typeof c === "string").join(" ");
              else value = String(rendered.props.children);
            }
          } catch {
            value = row[col.dataIndex] ?? "";
          }
        } else value = row[col.dataIndex] ?? "";

        if (typeof value === "object" && value !== null)
          value = JSON.stringify(value);

        return value;
      })
    );

    const topTitle = colTitle || title || `Отчёт ${index + 1}`;

    // 🧮 Добавляем summary, если есть
    let summaryRows = [];

    if (table.summary) {
      if (Array.isArray(table.summary)) {
        summaryRows = table.summary.map((row) => {
          if (Array.isArray(row)) {
            // Массив массивов, всё ок
            return row.map((cell) => (cell ?? "").toString());
          } else if (typeof row === "object" && row !== null) {
            // Объект totals → превращаем в массив значений по колонкам
            return columns.map((col) => {
              if (col.dataIndex && row[col.dataIndex] !== undefined) {
                return row[col.dataIndex]?.toString() ?? "";
              }
              if (col.key === "label") return "ИТОГО:";
              if (col.dataIndex === "salaryPercent") return "-";
              return "";
            });
          } else {
            // одиночное число/строка
            return [row?.toString() ?? ""];
          }
        });
      } else if (typeof table.summary === "object" && table.summary !== null) {
        // Если summary один объект totals
        summaryRows.push(
          columns.map((col) => {
            if (col.dataIndex && table.summary[col.dataIndex] !== undefined) {
              return table.summary[col.dataIndex]?.toString() ?? "";
            }
            if (col.key === "label") return "ИТОГО:";
            if (col.dataIndex === "salaryPercent") return "-";
            return "";
          })
        );
      }
    }


    const sheetData = [
      [topTitle],
      [],
      headers,
      ...rows,
      ...(summaryRows.length ? [[""], ...summaryRows] : []), // пустая строка перед summary
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Объединяем заголовок
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    ];

    // 🖋 Стилизация
    const borderStyle = {
      top: { style: "thin", color: { rgb: "999999" } },
      left: { style: "thin", color: { rgb: "999999" } },
      bottom: { style: "thin", color: { rgb: "999999" } },
      right: { style: "thin", color: { rgb: "999999" } },
    };

    // Заголовок
    const titleCell = worksheet["A1"];
    if (titleCell) {
      titleCell.s = {
        font: { bold: true, sz: 16 },
        alignment: { horizontal: "center", vertical: "center" },
      };
    }

    // Шапка колонок
    headers.forEach((_, i) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 2, c: i })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          alignment: { horizontal: "center" },
          fill: { fgColor: { rgb: "2f6f00" } },
          border: borderStyle,
        };
      }
    });

    // Основные данные
    rows.forEach((_, rIndex) => {
      headers.forEach((_, cIndex) => {
        const cell = worksheet[XLSX.utils.encode_cell({ r: 3 + rIndex, c: cIndex })];
        if (cell) {
          cell.s = {
            alignment: { horizontal: "left" },
            border: borderStyle,
          };
        }
      });
    });

    // 🟩 Стилизация summary
    if (summaryRows.length) {
      const summaryStartRow = 4 + rows.length; // первая строка после пустой
      summaryRows.forEach((_, rIndex) => {
        headers.forEach((_, cIndex) => {
          const cell = worksheet[XLSX.utils.encode_cell({ r: summaryStartRow + rIndex, c: cIndex })];
          if (cell) {
            cell.s = {
          font: { bold: true },
          alignment: { horizontal: cIndex === 0 ? "left" : "right" },
          fill: { fgColor: { rgb: "E8F5E9" } },
          border: borderStyle,
            };
          }
        });
      });
    }

    // Ширина колонок
    const colWidths = headers.map((_, colIndex) => {
      const maxLen = Math.max(
        headers[colIndex]?.length || 10,
        ...rows.map((r) => String(r[colIndex] || "").length)
      );
      return { wch: Math.min(maxLen + 2, 40) };
    });
    worksheet["!cols"] = colWidths;

    // Название листа
    let safeTitle = (title || topTitle)?.replace(/[\\/:*?"<>|]/g, "_") || `Sheet${index + 1}`;
    safeTitle = safeTitle.slice(0, 25).trim();

    let uniqueTitle = safeTitle;
    let counter = 2;
    while (workbook.SheetNames.includes(uniqueTitle)) {
      const suffix = ` (${counter++})`;
      uniqueTitle = safeTitle.slice(0, 31 - suffix.length) + suffix;
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, uniqueTitle);
  });

  XLSX.writeFile(workbook, fileName);
};
